import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  companiesAPI,
  devicesAPI,
  usersAPI,
  depotsAPI,
  notificationsAPI,
  auditLogsAPI,
  boxAssignmentsAPI,
} from '../services/api';
import MapWidget from '../components/MapWidget';
import './Dashboard.css';
import { useAuthStore } from '../store/authStore';
import { formatDateTimeForTimezone } from '../utils/datetime';

export default function Dashboard() {
  const [metrics, setMetrics] = useState({
    companiesCount: 0,
    devicesCount: 0,
    usersCount: 0,
    depotsCount: 0,
    assignedDevices: 0,
    unassignedDevices: 0,
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { timezone } = useAuthStore();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        companiesAPI.getAll(),
        devicesAPI.getAll(),
        usersAPI.getAll(),
        depotsAPI.getAll(),
        notificationsAPI.getAll({}),
        auditLogsAPI.getAll({}),
        boxAssignmentsAPI.getAll(),
      ]);

      const [companiesRes, devicesRes, usersRes, depotsRes, notificationsRes, logsRes, assignmentsRes] = results;

      // Extract data safely - 403 errors are expected for modules user doesn't have access to
      const companies = companiesRes.status === 'fulfilled' ? companiesRes.value.data : [];
      const devices = devicesRes.status === 'fulfilled' ? devicesRes.value.data : [];
      const users = usersRes.status === 'fulfilled' ? usersRes.value.data : [];
      const depots = depotsRes.status === 'fulfilled' ? depotsRes.value.data : [];
      const notifications = notificationsRes.status === 'fulfilled' ? notificationsRes.value.data : [];
      const logs = logsRes.status === 'fulfilled' ? logsRes.value.data : [];
      const assignments = assignmentsRes.status === 'fulfilled' ? assignmentsRes.value.data : [];

      // Calculate box assignment metrics. A box is assigned only when its
      // DeviceAssignment has a depot_id.
      const assignedDevicesCount = assignments.filter(a => a.depot_id).length;
      const unassignedDevices = assignments.filter(a => !a.depot_id).length;

      setMetrics({
        companiesCount: companies.length || 0,
        devicesCount: devices.length || 0,
        usersCount: users.length || 0,
        depotsCount: depots.length || 0,
        assignedDevices: assignedDevicesCount || 0,
        unassignedDevices: unassignedDevices || 0,
      });

      // Only set recent logs/notifications if available
      if (logs && logs.length > 0) {
        setRecentLogs(logs.slice(0, 5));
      }

      if (notifications && notifications.length > 0) {
        setRecentNotifications(notifications.slice(0, 5));
      }
    } catch (error) {
      // Only show error if it's not a permission issue
      if (error.response?.status !== 403) {
        toast.error('Error loading dashboard data');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <h1 className="page-title">Dashboard</h1>
        <p>Loading metrics...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <h1 className="page-title">Dashboard</h1>

      

      {/* Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card metric-card-blue">
          <div className="metric-icon">🏢</div>
          <h3>Total Companies</h3>
          <p className="metric-value">{metrics.companiesCount}</p>
        </div>

        <div className="metric-card metric-card-green">
          <div className="metric-icon">📱</div>
           <h3>Total Seals</h3>
          <p className="metric-value">{metrics.devicesCount}</p>
        </div>

        <div className="metric-card metric-card-red">
          <div className="metric-icon">👥</div>
          <h3>Total Users</h3>
          <p className="metric-value">{metrics.usersCount}</p>
        </div>

        <div className="metric-card metric-card-orange">
          <div className="metric-icon">📦</div>
          <h3>Total Depots</h3>
          <p className="metric-value">{metrics.depotsCount}</p>
        </div>
      </div>

      {/* Device Assignment Status Cards */}
      <div className="device-status-section">
         <h2 style={{ marginBottom: '20px', color: '#2c3e50' }}>Seal Assignment Status</h2>
        <div className="status-grid">
          <div className="status-card status-assigned">
            <div className="status-icon">📌</div>
             <h3>Assigned Seals</h3>
            <p className="status-value">{metrics.assignedDevices}</p>
            <p className="status-percentage">
              {metrics.devicesCount > 0 
                ? Math.round((metrics.assignedDevices / metrics.devicesCount) * 100) 
                : 0}%
            </p>
          </div>

          <div className="status-card status-unassigned">
            <div className="status-icon">⊘</div>
             <h3>Unassigned Seals</h3>
            <p className="status-value">{metrics.unassignedDevices}</p>
            <p className="status-percentage">
              {metrics.devicesCount > 0 
                ? Math.round((metrics.unassignedDevices / metrics.devicesCount) * 100) 
                : 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Main row: map on the left, activity/notifications on the right */}
      <div className="dashboard-main-row">
        <div className="dashboard-main-left">
          <section className="dashboard-map-section">
            <h2 className="section-title">Geographic Overview</h2>
            <MapWidget />
          </section>
        </div>

        <div className="dashboard-main-right">
          <div className="dashboard-sections">
            <section className="dashboard-section">
              <h2 className="section-title">Recent Activity</h2>
              <p style={{ marginTop: 4, marginBottom: 8, fontSize: 12, color: '#6b7280' }}>
                Times shown in your timezone: <strong>{timezone || 'UTC'}</strong>
              </p>
              <ul className="activity-list">
                {recentLogs.length === 0 ? (
                  <li className="empty-state">No recent activity.</li>
                ) : (
                  recentLogs.map((log) => (
                    <li key={log.id}>
                      <div
                        className="activity-badge"
                        style={{ backgroundColor: getActionColor(log.action) }}
                      >
                        {log.action[0]}
                      </div>
                      <div className="activity-content">
                        <p className="activity-title">
                          {log.action} - {log.entity_type} (#{log.entity_id})
                        </p>
                        <p className="activity-time">
                          {formatDateTimeForTimezone(log.created_at, timezone)}
                        </p>
                        {log.description && (
                          <p className="activity-description">{log.description}</p>
                        )}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </section>

            <section className="dashboard-section">
              <h2 className="section-title">Recent Notifications</h2>
              <ul className="activity-list">
                {recentNotifications.length === 0 ? (
                  <li className="empty-state">No recent notifications.</li>
                ) : (
                  recentNotifications.map((notification) => (
                    <li key={notification.id}>
                      <span>{notification.message}</span>
                      <span className="activity-timestamp">
                        {formatDateTimeForTimezone(notification.created_at, timezone)}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>

          <div className="dashboard-summary">
            <div className="summary-stat">
              <h4>System Health</h4>
              <div className="health-indicator">
                <div className="health-status status-good">●</div>
                <span>All systems operational</span>
              </div>
            </div>
            <div className="summary-stat">
              <h4>Last Updated</h4>
              <p>
                {new Date().toLocaleTimeString(undefined, {
                  timeZone: timezone || 'UTC',
                })}
              </p>
            </div>
            <div className="summary-stat">
              <h4>Active Sessions</h4>
              <p>1</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getActionColor(action) {
  switch (action) {
    case 'CREATE':
      return '#27ae60';
    case 'UPDATE':
      return '#3498db';
    case 'DELETE':
      return '#e74c3c';
    default:
      return '#95a5a6';
  }
}
